import { batchCreateRelationship, getNestedOrFloatingElementById } from '@fs/zion-sls'

/**
 * Generate SLS dispatches from the relationships map
 * This is the single source of truth for creating relationship dispatches after all reviews are complete
 *
 * @param {Object} params
 * @param {Object} params.relationshipsMapApi - The relationships map API
 * @param {Object} params.images - SLS images state
 * @param {string} params.currentImageArk - Current partition key
 * @param {string} params.mainPersonId - ID of the main person (focused person)
 * @param {string} params.targetRecordId - Target record ID for new persons
 * @returns {Array} Array of SLS dispatches ready to be saved
 */
export const generateDispatchesFromRelationshipsMap = ({
  relationshipsMapApi,
  images,
  currentImageArk,
  mainPersonId,
  targetRecordId,
}) => {
  if (!relationshipsMapApi || !images || !currentImageArk || !mainPersonId) {
    return []
  }

  const allRelationships = relationshipsMapApi.getAllRelationships()
  const dispatches = []

  allRelationships.forEach((relationship) => {
    const { firstPersonId, secondPersonId, relationshipType, relationshipDirection } = relationship

    // Get person objects (or create mock objects for new persons)
    let firstPerson = getNestedOrFloatingElementById(images, firstPersonId)
    let secondPerson = getNestedOrFloatingElementById(images, secondPersonId)

    // If person doesn't exist, create a mock object (new person)
    if (!firstPerson) {
      firstPerson = {
        id: firstPersonId,
        partitionKey: currentImageArk,
        recordId: targetRecordId,
        primary: firstPersonId === mainPersonId,
      }
    }

    if (!secondPerson) {
      secondPerson = {
        id: secondPersonId,
        partitionKey: currentImageArk,
        recordId: targetRecordId,
        primary: secondPersonId === mainPersonId,
      }
    }

    // Determine person1 and person2 based on relationship direction
    let person1Id
    let person2Id
    let person1PartitionKey
    let person2PartitionKey

    if (relationshipDirection === 'FIRST') {
      // FIRST means: firstPerson is person1, secondPerson is person2
      person1Id = firstPersonId
      person2Id = secondPersonId
      person1PartitionKey = firstPerson.partitionKey
      person2PartitionKey = secondPerson.partitionKey
    } else {
      // SECOND means: secondPerson is person1, firstPerson is person2
      person1Id = secondPersonId
      person2Id = firstPersonId
      person1PartitionKey = secondPerson.partitionKey
      person2PartitionKey = firstPerson.partitionKey
    }

    // Generate dispatches for this relationship
    const relationshipDispatches = batchCreateRelationship({
      partitionKey: currentImageArk,
      person1ID: person1Id,
      person2ID: person2Id,
      person1PartitionKey,
      person2PartitionKey,
      relType: relationshipType,
    })

    if (relationshipDispatches) {
      dispatches.push(...relationshipDispatches)
    }
  })

  return dispatches
}
